import { useEffect, useRef } from "react";
import { Routes, Route, Navigate, useNavigate, useLocation, useParams } from "react-router-dom";
import { Security, LoginCallback, useOktaAuth } from "@okta/okta-react";
import { AuthProvider, useAuthContext } from "./context/AuthContext";
import { useAuth } from "./context/useAuth";
import { authClient } from "./utils/authClient";
import NavBar from "./components/shared/NavBar/NavBar";
import DisclaimerPage from "./pages/DisclaimerPage/DisclaimerPage";

// Find Your Ohio pages
import DashboardPage       from "./components/find-your-ohio/DashboardPage/DashboardPage";
import CandidatesPage      from "./components/find-your-ohio/CandidatesPage/CandidatesPage";
import CandidateDetailPage from "./components/find-your-ohio/CandidateDetailPage/CandidateDetailPage";
import ReferralsPage        from "./components/find-your-ohio/ReferralsPage/ReferralsPage";
import ReferralDetailPage  from "./components/find-your-ohio/ReferralDetailPage/ReferralDetailPage";
import InfluencedRelocationPage       from "./components/find-your-ohio/InfluencedRelocationPage/InfluencedRelocationPage";
import InfluencedRelocationDetailPage from "./components/find-your-ohio/InfluencedRelocationDetailPage/InfluencedRelocationDetailPage";
import InfluencedPlacementPage        from "./components/find-your-ohio/InfluencedPlacementPage/InfluencedPlacementPage";
import InfluencedPlacementDetailPage  from "./components/find-your-ohio/InfluencedPlacementDetailPage/InfluencedPlacementDetailPage";
import ClientPlacementPage            from "./components/find-your-ohio/ClientPlacementPage/ClientPlacementPage";
import ClientPlacementDetailPage      from "./components/find-your-ohio/ClientPlacementDetailPage/ClientPlacementDetailPage";
import ClientsPage         from "./components/find-your-ohio/ClientsPage/ClientsPage";
import UsersPage             from "./components/find-your-ohio/UsersPage/UsersPage";
import SupportHubPage       from "./components/find-your-ohio/SupportHubPage/SupportHubPage";
import FeedbackPage         from "./components/find-your-ohio/FeedbackPage/FeedbackPage";

// AI Ready Ohio pages
import ARODashboardPage      from "./components/ai-ready-ohio/DashboardPage/DashboardPage";
import PeoplePage            from "./components/ai-ready-ohio/PeoplePage/PeoplePage";
import PersonDetailPage      from "./components/ai-ready-ohio/PersonDetailPage/PersonDetailPage";
import CertificatesPage      from "./components/ai-ready-ohio/CertificatesPage/CertificatesPage";
import CertificateDetailPage from "./components/ai-ready-ohio/CertificateDetailPage/CertificateDetailPage";
import EventsPage            from "./components/ai-ready-ohio/EventsPage/EventsPage";
import ImportPage            from "./components/ai-ready-ohio/ImportPage/ImportPage";
import ReportsPage           from "./components/ai-ready-ohio/ReportsPage/ReportsPage";
import AuditPage             from "./components/ai-ready-ohio/AuditPage/AuditPage";
import AROSupportHubPage    from "./components/ai-ready-ohio/SupportHubPage/AROSupportHubPage";
import QuarantinePage        from "./components/ai-ready-ohio/QuarantinePage/QuarantinePage";
import QuarantineDetailPage  from "./components/ai-ready-ohio/QuarantineDetailPage/QuarantineDetailPage";

import { canAccessQuarantine, canAccessUsers } from "./utils/quarantineAccess";

import "./App.css";

// ── Auth utilities ────────────────────────────────────────

const restoreOriginalUri = async (_oktaAuth: any, _originalUri: string) => {
  window.location.replace("/find-your-ohio/analytics");
};

function Loading() {
  return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100vh" }}>
      <div className="aro-spinner" />
    </div>
  );
}

function AuthErrorHandler({ error: _error }: { error: Error }) {
  useEffect(() => {
    sessionStorage.removeItem("okta-pkce-storage");
    sessionStorage.removeItem("okta-transaction-storage");
    window.location.replace("/");
  }, []);
  return <Loading />;
}

function PublicRoute({ children }: { children: React.ReactNode }) {
  const { authState } = useOktaAuth();
  if (!authState || authState.isPending) return <Loading />;
  return authState.isAuthenticated
    ? <Navigate to="/find-your-ohio/analytics" replace />
    : <>{children}</>;
}

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { authState } = useOktaAuth();
  if (!authState || authState.isPending) return <Loading />;
  if (!authState.isAuthenticated) return <Navigate to="/" replace />;
  return <>{children}</>;
}

// ── Portal guards ─────────────────────────────────────────

function FyoGuard({ children }: { children: React.ReactNode }) {
  const { portal_access } = useAuth();
  if (portal_access === "ai_ready_ohio") return <Navigate to="/ai-ready-ohio/analytics" replace />;
  return <>{children}</>;
}

function AroGuard({ children }: { children: React.ReactNode }) {
  const { portal_access } = useAuth();
  if (portal_access === "find_your_ohio") return <Navigate to="/find-your-ohio/analytics" replace />;
  return <>{children}</>;
}

// ── FYO route components ──────────────────────────────────

function FyoCandidateDetailRoute() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  return (
    <CandidateDetailPage
      candidateId={id!}
      onBack={() => navigate("/find-your-ohio/candidates")}
    />
  );
}

function FyoReferralDetailRoute() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  return (
    <ReferralDetailPage
      opportunityResourceId={id!}
      onBack={() => navigate("/find-your-ohio/direct_placement")}
      onViewCandidate={(cid) => navigate(`/find-your-ohio/candidates/${cid}`)}
    />
  );
}

function FyoInfluencedRelocationDetailRoute() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  return (
    <InfluencedRelocationDetailPage
      candidateId={id!}
      onBack={() => navigate("/find-your-ohio/linkedin-tracked-ohio-relocations")}
      onViewCandidate={(cid) => navigate(`/find-your-ohio/candidates/${cid}`)}
    />
  );
}

function FyoInfluencedPlacementDetailRoute() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  return (
    <InfluencedPlacementDetailPage
      candidateId={id!}
      onBack={() => navigate("/find-your-ohio/linkedin-tracked-ohio-hires")}
      onViewCandidate={(cid) => navigate(`/find-your-ohio/candidates/${cid}`)}
    />
  );
}

function FyoClientPlacementDetailRoute() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  return (
    <ClientPlacementDetailPage
      candidateId={id!}
      onBack={() => navigate("/find-your-ohio/linkedin-tracked-fyo-company-hires")}
      onViewCandidate={(cid) => navigate(`/find-your-ohio/candidates/${cid}`)}
    />
  );
}

// ── ARO route components ──────────────────────────────────

function AroPersonDetailRoute() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  return (
    <PersonDetailPage
      personId={id!}
      onBack={() => navigate("/ai-ready-ohio/people")}
      onSelectCertificate={() => {}}
    />
  );
}

function AroCertificatesRoute() {
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state as { eventId?: string } | null;
  return (
    <CertificatesPage
      initialEventId={state?.eventId}
      onSelectCertificate={() => {}}
      onSelectPerson={(id) => navigate(`/ai-ready-ohio/people/${id}`)}
    />
  );
}

function AroCertificateDetailRoute() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state as { back?: string; backLabel?: string } | null;
  return (
    <CertificateDetailPage
      certificateId={id!}
      onBack={() => navigate(state?.back ?? "/ai-ready-ohio/certificates")}
      onSelectPerson={(personId) => navigate(`/ai-ready-ohio/people/${personId}`)}
    />
  );
}

function AroQuarantineDetailRoute() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  return (
    <QuarantineDetailPage
      quarantineId={id!}
      onBack={() => navigate("/ai-ready-ohio/quarantine")}
    />
  );
}

// ── App ───────────────────────────────────────────────────

function AppContent() {
  const user = useAuth();
  const { email, portal_access } = user;
  const { loading, error: authError } = useAuthContext();
  const navigate = useNavigate();
  const location = useLocation();
  const lastPortal = useRef<"fyo" | "aro">("fyo");
  const rawPortal: "fyo" | "aro" = location.pathname.startsWith("/ai-ready-ohio") ? "aro" : "fyo";
  if (location.pathname !== "/users") lastPortal.current = rawPortal;
  const portal = location.pathname === "/users" ? lastPortal.current : rawPortal;

  useEffect(() => {
    if (loading) return;
    const path = location.pathname;
    if (portal_access === "ai_ready_ohio" && !path.startsWith("/ai-ready-ohio") && path !== "/users") {
      navigate("/ai-ready-ohio/analytics", { replace: true });
    } else if (portal_access === "find_your_ohio" && path.startsWith("/ai-ready-ohio") && !path.startsWith("/ai-ready-ohio/quarantine")) {
      navigate("/find-your-ohio/analytics", { replace: true });
    }
  }, [loading, portal_access]); // eslint-disable-line react-hooks/exhaustive-deps

  const homePath = portal_access === "ai_ready_ohio" ? "/ai-ready-ohio/analytics" : "/find-your-ohio/analytics";

  if (loading) return <Loading />;
  if (authError || user?.is_active === false) return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: "100vh", gap: 12 }}>
      <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700 }}>Access Denied</h2>
      <p style={{ margin: 0, color: "#6b7280" }}>
        {authError
          ? "Your account is not registered. Please contact a site administrator."
          : "Your account has been deactivated. Please contact a site administrator."}
      </p>
    </div>
  );

  return (
    <div className="app">
      <NavBar
        currentPath={location.pathname}
        onNavigate={(to) => navigate(to)}
        portal={portal}
        onSwitchPortal={(p) => navigate(p === "aro" ? "/ai-ready-ohio/analytics" : "/find-your-ohio/analytics")}
        portalAccess={portal_access}
      />

      <main className="app__main">
        <Routes>
          {/* Root redirects */}
          <Route path="/"    element={<Navigate to={homePath} replace />} />
          <Route path="/find-your-ohio" element={<Navigate to="/find-your-ohio/analytics" replace />} />
          <Route path="/ai-ready-ohio" element={<Navigate to="/ai-ready-ohio/analytics" replace />} />

          {/* Users page — restricted to a single allowlist, not role-based */}
          <Route path="/users" element={
            canAccessUsers(email) ? <UsersPage /> : <Navigate to={homePath} replace />
          } />

          {/* FYO routes */}
          <Route path="/find-your-ohio/analytics" element={
            <FyoGuard><DashboardPage onNavigate={(to, state) => navigate(to, { state })} /></FyoGuard>
          } />
          <Route path="/find-your-ohio/candidates" element={
            <FyoGuard><CandidatesPage onSelect={(id) => navigate(`/find-your-ohio/candidates/${id}`)} /></FyoGuard>
          } />
          <Route path="/find-your-ohio/candidates/:id" element={
            <FyoGuard><FyoCandidateDetailRoute /></FyoGuard>
          } />
          <Route path="/find-your-ohio/direct_placement" element={
            <FyoGuard>
              <ReferralsPage
                onViewCandidate={(id) => navigate(`/find-your-ohio/candidates/${id}`)}
                onSelect={(oid) => navigate(`/find-your-ohio/direct_placement/${oid}`)}
              />
            </FyoGuard>
          } />
          <Route path="/find-your-ohio/direct_placement/:id" element={
            <FyoGuard><FyoReferralDetailRoute /></FyoGuard>
          } />
          <Route path="/find-your-ohio/linkedin-tracked-ohio-relocations" element={
            <FyoGuard>
              <InfluencedRelocationPage
                onViewCandidate={(id) => navigate(`/find-your-ohio/candidates/${id}`)}
                onSelect={(id) => navigate(`/find-your-ohio/linkedin-tracked-ohio-relocations/${id}`)}
              />
            </FyoGuard>
          } />
          <Route path="/find-your-ohio/linkedin-tracked-ohio-relocations/:id" element={
            <FyoGuard><FyoInfluencedRelocationDetailRoute /></FyoGuard>
          } />
          <Route path="/find-your-ohio/linkedin-tracked-ohio-hires" element={
            <FyoGuard>
              <InfluencedPlacementPage
                onViewCandidate={(id) => navigate(`/find-your-ohio/candidates/${id}`)}
                onSelect={(id) => navigate(`/find-your-ohio/linkedin-tracked-ohio-hires/${id}`)}
              />
            </FyoGuard>
          } />
          <Route path="/find-your-ohio/linkedin-tracked-ohio-hires/:id" element={
            <FyoGuard><FyoInfluencedPlacementDetailRoute /></FyoGuard>
          } />
          <Route path="/find-your-ohio/linkedin-tracked-fyo-company-hires" element={
            <FyoGuard>
              <ClientPlacementPage
                onViewCandidate={(id) => navigate(`/find-your-ohio/candidates/${id}`)}
                onSelect={(id) => navigate(`/find-your-ohio/linkedin-tracked-fyo-company-hires/${id}`)}
              />
            </FyoGuard>
          } />
          <Route path="/find-your-ohio/linkedin-tracked-fyo-company-hires/:id" element={
            <FyoGuard><FyoClientPlacementDetailRoute /></FyoGuard>
          } />
          <Route path="/find-your-ohio/companies" element={
            <FyoGuard><ClientsPage /></FyoGuard>
          } />
          <Route path="/find-your-ohio/feedback" element={<FyoGuard><FeedbackPage /></FyoGuard>} />
          <Route path="/find-your-ohio/help"     element={<FyoGuard><SupportHubPage /></FyoGuard>} />
          <Route path="/find-your-ohio/faq"      element={<Navigate to="/find-your-ohio/help?tab=faq"      replace />} />
          <Route path="/find-your-ohio/training" element={<Navigate to="/find-your-ohio/help?tab=training" replace />} />

          {/* AI Ready Ohio Quarantine — restricted to a single allowlisted email */}
          <Route path="/ai-ready-ohio/quarantine" element={
            canAccessQuarantine(email)
              ? <QuarantinePage onSelect={(id) => navigate(`/ai-ready-ohio/quarantine/${id}`)} />
              : <Navigate to={homePath} replace />
          } />
          <Route path="/ai-ready-ohio/quarantine/:id" element={
            canAccessQuarantine(email) ? <AroQuarantineDetailRoute /> : <Navigate to={homePath} replace />
          } />

          {/* ARO routes */}
          <Route path="/ai-ready-ohio/analytics" element={
            <AroGuard><ARODashboardPage /></AroGuard>
          } />
          <Route path="/ai-ready-ohio/people" element={
            <AroGuard><PeoplePage onSelect={(id) => navigate(`/ai-ready-ohio/people/${id}`)} /></AroGuard>
          } />
          <Route path="/ai-ready-ohio/people/:id" element={
            <AroGuard><AroPersonDetailRoute /></AroGuard>
          } />
          <Route path="/ai-ready-ohio/certificates" element={
            <AroGuard><AroCertificatesRoute /></AroGuard>
          } />
          {/* <Route path="/ai-ready-ohio/certificates/:id" element={
            <AroGuard><AroCertificateDetailRoute /></AroGuard>
          } /> */}
          <Route path="/ai-ready-ohio/events" element={
            <AroGuard><EventsPage onViewCertificates={(id) => navigate("/ai-ready-ohio/certificates", { state: { eventId: id } })} /></AroGuard>
          } />
          <Route path="/ai-ready-ohio/import"  element={<AroGuard><ImportPage /></AroGuard>} />
          <Route path="/ai-ready-ohio/reports" element={
            canAccessQuarantine(email) ? <AroGuard><ReportsPage /></AroGuard> : <Navigate to={homePath} replace />
          } />
          <Route path="/ai-ready-ohio/audit"   element={<AroGuard><AuditPage /></AroGuard>} />
          <Route path="/ai-ready-ohio/help"     element={<AroGuard><AROSupportHubPage /></AroGuard>} />
          <Route path="/ai-ready-ohio/faq"      element={<Navigate to="/ai-ready-ohio/help?tab=faq"      replace />} />
          <Route path="/ai-ready-ohio/training" element={<Navigate to="/ai-ready-ohio/help?tab=training" replace />} />

          {/* Catch-all */}
          <Route path="*" element={<Navigate to={homePath} replace />} />
        </Routes>
      </main>
    </div>
  );
}

// ── Root ──────────────────────────────────────────────────

export default function App() {
  return (
    <Security oktaAuth={authClient} restoreOriginalUri={restoreOriginalUri}>
      <Routes>
        <Route path="/" element={
          <PublicRoute><DisclaimerPage /></PublicRoute>
        } />
        <Route path="/login/callback" element={
          <LoginCallback
            loadingElement={<Loading />}
            errorComponent={AuthErrorHandler}
          />
        } />
        <Route path="*" element={
          <ProtectedRoute>
            <AuthProvider>
              <AppContent />
            </AuthProvider>
          </ProtectedRoute>
        } />
      </Routes>
    </Security>
  );
}
